<template>
  <div>
    <Panel
      v-if="canManageUsers"
      :icon="['fas', 'users']"
    >
      <template #header>
        <span>Manage Users</span>
      </template>

      <template #content>
        <b-modal
          id="add-user-modal"
          ref="add-user-modal"
          size="lg"
          :hide-footer="true"
          header-class="custom-modal"
          body-class="custom-modal"
          footer-class="custom-modal"
          title="Add User"
          button-size="sm"
        >
          <validation-observer
            ref="formValidation"
            v-slot="{ handleSubmit }"
          >
            <b-form
              novalidate
              @submit.stop.prevent="handleSubmit(submitForm)"
            >
              <BaseFormInput
                v-model.trim="firstName"
                :horizontal="true"
                :small="true"
                name="First Name"
                label="First Name"
                placeholder="Enter first name"
                type="text"
                required
                width="7"
              />
              <BaseFormInput
                v-model.trim="lastName"
                :horizontal="true"
                :small="true"
                name="Last Name"
                label="Last Name"
                placeholder="Enter last name"
                type="text"
                required
                width="7"
              />
              
              <BaseFormInput
                v-model.trim="email"
                :horizontal="true"
                :small="true"
                name="Email"
                label="Email"
                placeholder="Enter user email address"
                type="email"
                rules="email"
                vid="email"
                required
                width="7"
                lazy
                @blur="onBlurEmail"
              />

              <fieldset class="form-group row">
                <label class="col-sm-3 col-form-label">Permissions</label>
                <div class="col-sm-7">
                  <div
                    v-for="permission in permissionOptions"
                    :key="permission.value"
                    class="mb-2"
                  >
                    <b-form-checkbox
                      :id="'add-user-permission-' + (permission.value)"
                      v-model="permissions"
                      :value="permission.value"
                      :disabled="permission.value === 'billing' && permissions.includes('admin')"
                      :data-cy="'select-add-user-permission-' + (permission.value)"
                    >
                      {{ permission.text }}
                    </b-form-checkbox>
                    <AccountPermissionExplainer :value="permission.value" />
                  </div>
                  <p v-if="!permissions.length" role="alert" class="text-danger">
                    Select at least one account permission.
                  </p>
                  <AccountPermissionPreview
                    :before="[]"
                    :after="permissions"
                    :options="permissionOptions"
                  />
                  <b-form-checkbox v-model="confirmed" class="mt-3">
                    I confirm this user will receive only the access shown above.
                  </b-form-checkbox>
                </div>
              </fieldset>

              <BaseButtonLoader
                horizontal
                :loading="isLoading"
                :disabled="!permissions.length || !confirmed"
                class="mt-4"
                button-class="custom-button--white-hover-grad pulse"
                type="submit"
                content-cols="9"
              >
                Submit
              </BaseButtonLoader>
            </b-form>
          </validation-observer>
        </b-modal>
        <div class="text-left">
          <BaseButtonLoader
            :loading="isLoading"
            class="ml-2"
            type="button"
            :custom-button="true"
            size="sm"
            variant="primary"
            @click="$bvModal.show('add-user-modal')"
          >
            Add User
          </BaseButtonLoader>
        </div>
      </template>
    </Panel>
    <Panel v-if="canManageUsers" :hide-header="canManageUsers">
      <template #header>
        <span>Users</span>
      </template>

      <template #content>
        <AccountUsers :items="accountUsers" />
      </template>
    </Panel>
  </div> 
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'
import dateFormatMixin from '@/mixins/dateFormatMixin'
import formInputMixin from '@/mixins/formInputMixin'
import AccountUsers from '@/components/account/AccountUsers.vue'
import AccountPermissionExplainer from '@/components/account/AccountPermissionExplainer.vue'
import AccountPermissionPreview from '@/components/account/AccountPermissionPreview.vue'
import { showErrorMessage, showSuccessMessage } from '@/notification-utils'
import { accountPermissionOptions, canonicalPermissions } from '@/store/account-permissions'

export default {
  name: 'UserManagement',

  title: 'Manage Users',

  components: {
    AccountPermissionExplainer,
    AccountPermissionPreview,
    AccountUsers,
  },
  
  mixins: [dateFormatMixin, formInputMixin],

  data() {
    return {
      isLoading: false,
      email: null, 
      firstName: null,
      lastName: null,
      permissions: [],
      confirmed: false
    }
  },

  computed: {
    ...mapState('account', [
      'accountUsers'
    ]),

    ...mapGetters('auth', ['hasPermission', 'isSellerAccount']),

    permissionOptions() {
      return accountPermissionOptions(this.isSellerAccount)
    },

    canManageUsers() {
      return this.hasPermission('admin')
    }
  },

  async created () {
    if (this.canManageUsers) await this.getAccountUsers()
  },

  watch: {
    permissions(selection) {
      this.confirmed = false
      if (selection.includes('admin') && !selection.includes('billing')) {
        this.permissions = canonicalPermissions(selection)
      }
    }
  },

  methods: {

    ...mapActions('account', [
      'getAccountUsers',
      'createAccountUser'
    ]),

    onBlurEmail() {
      this.email = this.email?.toLowerCase() || ''
    },

    async submitForm() {
      if (this.isLoading || !this.canManageUsers || !this.permissions.length || !this.confirmed) return
      try {
        this.isLoading = true

        const payload = { 
          email: this.email, 
          firstName: this.firstName,
          lastName: this.lastName,
          permissions: canonicalPermissions(this.permissions)
        }
        
        await this.createAccountUser(payload)

        showSuccessMessage("Successfuly added a user to the account")

        this.$bvModal.hide('add-user-modal')
        
        this.resetFormFields()
        
        await this.getAccountUsers()
      } catch (error) {
        showErrorMessage(`${error.response?.data?.title} ${JSON.stringify(error.response?.data?.errors) ?? ''}`)
      } finally {
        this.isLoading = false
      }
    },

    resetFormFields() {
      this.email = ''
      this.firstName = ''
      this.lastName = ''
      this.permissions = []
      this.confirmed = false
    }
  },
}
</script>

<style>
</style>